import { Dispatch, SetStateAction, useMemo, useState, useCallback, RefObject } from 'react';
import type { ActivityType, ProductKind } from 'types';
import { Button, ButtonGroup, Input, Select, useDebounce } from '@ff-it/ui';
import { productKindOptions } from 'options';
import type { FilterState } from './types';
import { companyFieldProps } from 'modules/core/company/components';
import { useOptions } from 'components';
import * as style from './Filter.css';
import { Icon } from 'components/Icon';
import { clearIcon } from 'styles/style.css';

export function useFilterState(
  type: ActivityType,
  kind?: ProductKind | ProductKind[],
): [fitler: FilterState, setFilter: Dispatch<SetStateAction<FilterState>>] {
  return useState<FilterState>({
    type,
    kind: kind ? (Array.isArray(kind) ? kind[0] : kind) : 'MEDIA',
    search: '',
    provider: null,
  });
}

export function useKindOptions(kind?: ProductKind | ProductKind[]): typeof productKindOptions | null {
  return useMemo(() => {
    if (!kind) {
      return productKindOptions;
    }
    if (Array.isArray(kind)) {
      return productKindOptions.filter((o) => kind.includes(o.value));
    }
    return null;
  }, [kind]);
}

type FilterContainerProps = {
  filter: FilterState;
  setFilter: Dispatch<SetStateAction<FilterState>>;
  kindOptions: typeof productKindOptions | null;
  searchInputRef: RefObject<HTMLInputElement>;
};

const providerFieldProps = {
  placeholder: 'Filter provider',
  ...companyFieldProps,
  classNames: {
    control: () => style.providerControl,
  },
  components: {
    DropdownIndicator: null,
  },
};

export function FilterContainer({ filter, setFilter, kindOptions, searchInputRef }: FilterContainerProps) {
  const [searchInput, setSearchInput] = useState(filter.search);
  const loadOptions = useOptions<any>('core/companies/', { include_branches: true });
  const updateFilterSearch = useCallback(
    (searchValue: string) => {
      setFilter((v) => ({ ...v, search: searchValue }));
    },
    [setFilter],
  );

  const debouncedUpdateFilter = useDebounce(updateFilterSearch, 500);

  const handleSearchChange = (value: string) => {
    setSearchInput(value);

    // If clearing search (empty value), execute immediately
    // Otherwise, use debounced function
    if (value === '') {
      debouncedUpdateFilter.cancel(); // Cancel any pending updates
      updateFilterSearch(value); // Execute immediately
    } else {
      debouncedUpdateFilter(value);
    }
  };

  return (
    <div className={style.container}>
      <div className={style.search}>
        <Input
          name="search"
          placeholder="Search vendors, outlets, products..."
          value={searchInput}
          onChange={(e) => handleSearchChange(e.target.value)}
          ref={searchInputRef}
        />
        {searchInput && <Icon icon="circle-xmark" className={clearIcon} onClick={() => handleSearchChange('')} />}
      </div>
      <div className={style.provider}>
        <Select
          value={filter.provider}
          loadOptions={loadOptions}
          onChange={(provider) => setFilter((v) => ({ ...v, provider }))}
          {...providerFieldProps}
        />
        {filter.provider && (
          <Icon
            icon="circle-xmark"
            className={clearIcon}
            onClick={() => setFilter((v) => ({ ...v, provider: null }))}
          />
        )}
      </div>
      {kindOptions && (
        <ButtonGroup ml="1">
          {kindOptions.map(({ value, label }) => (
            <Button
              key={value}
              variant="secondary"
              size="sm"
              active={value === filter.kind}
              onClick={() => setFilter((s) => ({ ...s, kind: value }))}
            >
              {label}
            </Button>
          ))}
        </ButtonGroup>
      )}
    </div>
  );
}
